function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { getActiveTransaction } from '@sentry/core';
import { browserPerformanceTimeOrigin, logger, htmlTreeAsString } from '@sentry/utils';
import { WINDOW } from '../types.js';
import { onCLS } from '../web-vitals/getCLS.js';
import { onFID } from '../web-vitals/getFID.js';
import { onLCP } from '../web-vitals/getLCP.js';
import { getVisibilityWatcher } from '../web-vitals/lib/getVisibilityWatcher.js';
import { observe } from '../web-vitals/lib/observe.js';
import { _startChild, isMeasurementValue } from './utils.js';
/**
 * Converts from milliseconds to seconds
 * @param time time in ms
 */

function msToSec(time) {
  return time / 1000;
}

function getBrowserPerformanceAPI() {
  return WINDOW && WINDOW.addEventListener && WINDOW.performance;
}

var _performanceCursor = 0;
var _measurements = {};

var _lcpEntry;

var _clsEntry;
/**
 * Start tracking web vitals
 *
 * @returns A function that forces web vitals collection
 */


function startTrackingWebVitals() {
  var performance = getBrowserPerformanceAPI();

  if (performance && browserPerformanceTimeOrigin) {
    if (performance.mark) {
      WINDOW.performance.mark('sentry-tracing-init');
    }

    _trackFID();

    var clsCallback = _trackCLS();

    var lcpCallback = _trackLCP();

    return function () {
      if (clsCallback) {
        clsCallback();
      }

      if (lcpCallback) {
        lcpCallback();
      }
    };
  }

  return function () {
    return undefined;
  };
}
/**
 * Start tracking long tasks.
 */


function startTrackingLongTasks() {
  var entryHandler = function entryHandler(entries) {
    var _iterator = _createForOfIteratorHelper(entries),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var entry = _step.value;
        var transaction = getActiveTransaction();

        if (!transaction) {
          return;
        }

        var startTime = msToSec(browserPerformanceTimeOrigin + entry.startTime);
        var duration = msToSec(entry.duration);
        transaction.startChild({
          description: 'Main UI thread blocked',
          op: 'ui.long-task',
          startTimestamp: startTime,
          endTimestamp: startTime + duration
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };

  observe('longtask', entryHandler);
}
/**
 * Start tracking interaction events.
 */


function startTrackingInteractions() {
  var entryHandler = function entryHandler(entries) {
    var _iterator2 = _createForOfIteratorHelper(entries),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var entry = _step2.value;
        var transaction = getActiveTransaction();

        if (!transaction) {
          return;
        }

        if (entry.name === 'click') {
          var startTime = msToSec(browserPerformanceTimeOrigin + entry.startTime);
          var duration = msToSec(entry.duration);
          transaction.startChild({
            description: htmlTreeAsString(entry.target),
            op: "ui.interaction.".concat(entry.name),
            startTimestamp: startTime,
            endTimestamp: startTime + duration
          });
        }
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
  };

  observe('event', entryHandler, {
    durationThreshold: 0
  });
}
/** Starts tracking the Cumulative Layout Shift on the current page. */


function _trackCLS() {
  // See:
  // https://web.dev/evolving-cls/
  // https://web.dev/cls-web-tooling/
  return onCLS(function (metric) {
    var entry = metric.entries.pop();

    if (!entry) {
      return;
    }

    (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding CLS');
    _measurements['cls'] = {
      value: metric.value,
      unit: ''
    };
    _clsEntry = entry;
  });
}
/** Starts tracking the Largest Contentful Paint on the current page. */


function _trackLCP() {
  return onLCP(function (metric) {
    var entry = metric.entries.pop();

    if (!entry) {
      return;
    }

    (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding LCP');
    _measurements['lcp'] = {
      value: metric.value,
      unit: 'millisecond'
    };
    _lcpEntry = entry;
  });
}
/** Starts tracking the First Input Delay on the current page. */


function _trackFID() {
  onFID(function (metric) {
    var entry = metric.entries.pop();

    if (!entry) {
      return;
    }

    var timeOrigin = msToSec(browserPerformanceTimeOrigin);
    var startTime = msToSec(entry.startTime);
    (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding FID');
    _measurements['fid'] = {
      value: metric.value,
      unit: 'millisecond'
    };
    _measurements['mark.fid'] = {
      value: timeOrigin + startTime,
      unit: 'second'
    };
  });
}
/** Add performance related spans to a transaction */


function addPerformanceEntries(transaction) {
  var performance = getBrowserPerformanceAPI();

  if (!performance || !WINDOW.performance.getEntries || !browserPerformanceTimeOrigin) {
    // Gatekeeper if performance API not available
    return;
  }

  (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Tracing] Adding & adjusting spans using Performance API');
  var timeOrigin = msToSec(browserPerformanceTimeOrigin);
  var performanceEntries = performance.getEntries();
  var responseStartTimestamp;
  var requestStartTimestamp; // eslint-disable-next-line @typescript-eslint/no-explicit-any

  performanceEntries.slice(_performanceCursor).forEach(function (entry) {
    var startTime = msToSec(entry.startTime);
    var duration = msToSec(entry.duration);

    if (transaction.op === 'navigation' && timeOrigin + startTime < transaction.startTimestamp) {
      return;
    }

    switch (entry.entryType) {
      case 'navigation':
        {
          _addNavigationSpans(transaction, entry, timeOrigin);

          responseStartTimestamp = timeOrigin + msToSec(entry.responseStart);
          requestStartTimestamp = timeOrigin + msToSec(entry.requestStart);
          break;
        }

      case 'mark':
      case 'paint':
      case 'measure':
        {
          _addMeasureSpans(transaction, entry, startTime, duration, timeOrigin); // capture web vitals


          var firstHidden = getVisibilityWatcher(); // Only report if the page wasn't hidden prior to the web vital.

          var shouldRecord = entry.startTime < firstHidden.firstHiddenTime;

          if (entry.name === 'first-paint' && shouldRecord) {
            (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding FP');
            _measurements['fp'] = {
              value: entry.startTime,
              unit: 'millisecond'
            };
          }

          if (entry.name === 'first-contentful-paint' && shouldRecord) {
            (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding FCP');
            _measurements['fcp'] = {
              value: entry.startTime,
              unit: 'millisecond'
            };
          }

          break;
        }

      case 'resource':
        {
          var resourceName = entry.name.replace(WINDOW.location.origin, '');

          _addResourceSpans(transaction, entry, resourceName, startTime, duration, timeOrigin);

          break;
        }
      // Ignore other entry types.
    }
  });
  _performanceCursor = Math.max(performanceEntries.length - 1, 0);

  _trackNavigator(transaction); // Measurements are only available for pageload transactions


  if (transaction.op === 'pageload') {
    // Generate TTFB (Time to First Byte), which measured as the time between the beginning of the transaction and the
    // start of the response in milliseconds
    if (typeof responseStartTimestamp === 'number') {
      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding TTFB');
      _measurements['ttfb'] = {
        value: (responseStartTimestamp - transaction.startTimestamp) * 1000,
        unit: 'millisecond'
      };

      if (typeof requestStartTimestamp === 'number' && requestStartTimestamp <= responseStartTimestamp) {
        // Capture the time spent making the request and receiving the first byte of the response.
        // This is the time between the start of the request and the start of the response in milliseconds.
        _measurements['ttfb.requestTime'] = {
          value: (responseStartTimestamp - requestStartTimestamp) * 1000,
          unit: 'millisecond'
        };
      }
    }

    ['fcp', 'fp', 'lcp'].forEach(function (name) {
      if (!_measurements[name] || timeOrigin >= transaction.startTimestamp) {
        return;
      } // The web vitals, fcp, fp, lcp, and ttfb, all measure relative to timeOrigin.
      // Unfortunately, timeOrigin is not captured within the transaction span data, so these web vitals will need
      // to be adjusted to be relative to transaction.startTimestamp.


      var oldValue = _measurements[name].value;
      var measurementTimestamp = timeOrigin + msToSec(oldValue); // normalizedValue should be in milliseconds

      var normalizedValue = Math.abs((measurementTimestamp - transaction.startTimestamp) * 1000);
      var delta = normalizedValue - oldValue;
      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("[Measurements] Normalized ".concat(name, " from ").concat(oldValue, " to ").concat(normalizedValue, " (").concat(delta, ")"));
      _measurements[name].value = normalizedValue;
    });
    var fidMark = _measurements['mark.fid'];

    if (fidMark && _measurements['fid']) {
      // create span for FID
      _startChild(transaction, {
        description: 'first input delay',
        endTimestamp: fidMark.value + msToSec(_measurements['fid'].value),
        op: 'ui.action',
        startTimestamp: fidMark.value
      }); // Delete mark.fid as we don't want it to be part of final payload


      delete _measurements['mark.fid'];
    } // If FCP is not recorded we should not record the cls value
    // according to the new definition of CLS.


    if (!('fcp' in _measurements)) {
      delete _measurements.cls;
    }

    Object.keys(_measurements).forEach(function (measurementName) {
      transaction.setMeasurement(measurementName, _measurements[measurementName].value, _measurements[measurementName].unit);
    });

    _tagMetricInfo(transaction);
  }

  _lcpEntry = undefined;
  _clsEntry = undefined;
  _measurements = {};
}
/** Create measure related spans */


function _addMeasureSpans(transaction, // eslint-disable-next-line @typescript-eslint/no-explicit-any
entry, startTime, duration, timeOrigin) {
  var measureStartTimestamp = timeOrigin + startTime;
  var measureEndTimestamp = measureStartTimestamp + duration;

  _startChild(transaction, {
    description: entry.name,
    endTimestamp: measureEndTimestamp,
    op: entry.entryType,
    startTimestamp: measureStartTimestamp
  });

  return measureStartTimestamp;
}
/** Instrument navigation entries */
// eslint-disable-next-line @typescript-eslint/no-explicit-any


function _addNavigationSpans(transaction, entry, timeOrigin) {
  ['unloadEvent', 'redirect', 'domContentLoadedEvent', 'loadEvent', 'connect'].forEach(function (event) {
    _addPerformanceNavigationTiming(transaction, entry, event, timeOrigin);
  });

  _addPerformanceNavigationTiming(transaction, entry, 'secureConnection', timeOrigin, 'TLS/SSL', 'connectEnd');

  _addPerformanceNavigationTiming(transaction, entry, 'fetch', timeOrigin, 'cache', 'domainLookupStart');

  _addPerformanceNavigationTiming(transaction, entry, 'domainLookup', timeOrigin, 'DNS');

  _addRequest(transaction, entry, timeOrigin);
}
/** Create performance navigation related spans */


function _addPerformanceNavigationTiming(transaction, // eslint-disable-next-line @typescript-eslint/no-explicit-any
entry, event, timeOrigin, description, eventEnd) {
  var end = eventEnd ? entry[eventEnd] : entry["".concat(event, "End")];
  var start = entry["".concat(event, "Start")];

  if (!start || !end) {
    return;
  }

  _startChild(transaction, {
    op: 'browser',
    description: description || event,
    startTimestamp: timeOrigin + msToSec(start),
    endTimestamp: timeOrigin + msToSec(end)
  });
}
/** Create request and response related spans */
// eslint-disable-next-line @typescript-eslint/no-explicit-any


function _addRequest(transaction, entry, timeOrigin) {
  _startChild(transaction, {
    op: 'browser',
    description: 'request',
    startTimestamp: timeOrigin + msToSec(entry.requestStart),
    endTimestamp: timeOrigin + msToSec(entry.responseEnd)
  });

  _startChild(transaction, {
    op: 'browser',
    description: 'response',
    startTimestamp: timeOrigin + msToSec(entry.responseStart),
    endTimestamp: timeOrigin + msToSec(entry.responseEnd)
  });
}
/** Create resource-related spans */


function _addResourceSpans(transaction, entry, resourceName, startTime, duration, timeOrigin) {
  // we already instrument based on fetch and xhr, so we don't need to
  // duplicate spans here.
  if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
    return;
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any


  var data = {};

  if ('transferSize' in entry) {
    data['Transfer Size'] = entry.transferSize;
  }

  if ('encodedBodySize' in entry) {
    data['Encoded Body Size'] = entry.encodedBodySize;
  }

  if ('decodedBodySize' in entry) {
    data['Decoded Body Size'] = entry.decodedBodySize;
  }

  if ('renderBlockingStatus' in entry) {
    data['resource.render_blocking_status'] = entry.renderBlockingStatus;
  }

  var startTimestamp = timeOrigin + startTime;
  var endTimestamp = startTimestamp + duration;

  _startChild(transaction, {
    description: resourceName,
    endTimestamp: endTimestamp,
    op: entry.initiatorType ? "resource.".concat(entry.initiatorType) : 'resource.other',
    startTimestamp: startTimestamp,
    data: data
  });
}
/**
 * Capture the information of the user agent.
 */


function _trackNavigator(transaction) {
  var navigator = WINDOW.navigator;

  if (!navigator) {
    return;
  } // track network connectivity


  var connection = navigator.connection;

  if (connection) {
    if (connection.effectiveType) {
      transaction.setTag('effectiveConnectionType', connection.effectiveType);
    }

    if (connection.type) {
      transaction.setTag('connectionType', connection.type);
    }

    if (isMeasurementValue(connection.rtt)) {
      _measurements['connection.rtt'] = {
        value: connection.rtt,
        unit: 'millisecond'
      };
    }
  }

  if (isMeasurementValue(navigator.deviceMemory)) {
    transaction.setTag('deviceMemory', "".concat(navigator.deviceMemory, " GB"));
  }

  if (isMeasurementValue(navigator.hardwareConcurrency)) {
    transaction.setTag('hardwareConcurrency', String(navigator.hardwareConcurrency));
  }
}
/** Add LCP / CLS data to transaction to allow debugging */


function _tagMetricInfo(transaction) {
  if (_lcpEntry) {
    (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding LCP Data'); // Capture Properties of the LCP element that contributes to the LCP.

    if (_lcpEntry.element) {
      transaction.setTag('lcp.element', htmlTreeAsString(_lcpEntry.element));
    }

    if (_lcpEntry.id) {
      transaction.setTag('lcp.id', _lcpEntry.id);
    }

    if (_lcpEntry.url) {
      // Trim URL to the first 200 characters.
      transaction.setTag('lcp.url', _lcpEntry.url.trim().slice(0, 200));
    }

    transaction.setTag('lcp.size', _lcpEntry.size);
  } // See: https://developer.mozilla.org/en-US/docs/Web/API/LayoutShift


  if (_clsEntry && _clsEntry.sources) {
    (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('[Measurements] Adding CLS Data');

    _clsEntry.sources.forEach(function (source, index) {
      return transaction.setTag("cls.source.".concat(index + 1), htmlTreeAsString(source.node));
    });
  }
}

export { _addMeasureSpans, _addResourceSpans, addPerformanceEntries, startTrackingInteractions, startTrackingLongTasks, startTrackingWebVitals };